import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

// Extend the Window interface to include native bridge methods
declare global {
  interface Window {
    Android?: {
      handleEvent(event: string | object): void;
    };
    webkit?: {
      messageHandlers: {
        handleEvent: {
          postMessage(event: string | object): void;
        };
      };
    };
    receiveTokenFromNative?: (token: string) => void;
    navigateToPath?: (path: string) => void;
  }
}

export enum NativeEventType {
  BRIDGE_READY = 'bridge_ready',
  VERIFICATION_SUCCESS = 'verification_success',
  VIDEO_VERIFICATION_SUCCESS = 'video_verification_success',
  VERIFICATION_FAILED = 'verification_failed',
  VERIFICATION_STEP_COMPLETED = 'verification_step_completed',
}

export interface NativeEvent {
  type: NativeEventType | string;
  data?: any;
  timestamp?: number;
}

const IS_SERVER = typeof window === 'undefined';

@Injectable({
  providedIn: 'root',
})
export class NativeBridgeService {
  private readonly TOKEN_KEY = 'auth_token';

  constructor() {
    this.checkNativeBridgeAvailability();
  }

  /**
   * Initialize native bridge handlers for token management and navigation
   */
  initializeNativeBridgeHandlers(router: Router): void {
    if (IS_SERVER) {
      return;
    }

    // Set up the receiveTokenFromNative function with safe localStorage access
    window.receiveTokenFromNative = (token: string) => {
      this.setLocalStorageItem(this.TOKEN_KEY, token);
    };

    // Add navigate function which will change window location
    window.navigateToPath = (path: string) => {
      window.location.href = path;
    };
  }

  /**
   * Safely get item from localStorage
   */
  private getLocalStorageItem(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.warn('Failed to access localStorage:', error);
      return null;
    }
  }

  /**
   * Safely set item in localStorage
   */
  private setLocalStorageItem(key: string, value: string): boolean {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (error) {
      console.warn('Failed to write to localStorage:', error);
      return false;
    }
  }

  /**
   * Get authentication token from localStorage
   */
  getAuthToken(): string | null {
    return this.getLocalStorageItem(this.TOKEN_KEY);
  }

  /**
   * Check if running in webview
   */
  isWebview(): boolean {
    return this.getLocalStorageItem('isCoinmateWebview') === 'true';
  }

  /**
   * Check if native bridge is available
   */
  private checkNativeBridgeAvailability(): void {
    if (this.isNativeBridgeAvailable()) {
      this.sendEventToNative(NativeEventType.BRIDGE_READY);
      console.log('Native bridge detected:', this.getNativePlatform());
    } else {
      console.warn('Native bridge not available. Running in web browser.');
    }
  }

  /**
   * Determine which native platform is available
   */
  private getNativePlatform(): 'ios' | 'android' | null {
    if (window.webkit?.messageHandlers?.handleEvent) {
      return 'ios';
    } else if (window.Android?.handleEvent) {
      return 'android';
    }
    return null;
  }

  /**
   * Check if any native bridge is available
   */
  isNativeBridgeAvailable(): boolean {
    if (IS_SERVER) {
      return false;
    }

    return !!(window.webkit?.messageHandlers?.handleEvent || window.Android?.handleEvent);
  }

  /**
   * Send event to native platform
   */
  sendEventToNative(event: string | NativeEvent): boolean {
    try {
      const eventData =
        typeof event === 'string'
          ? { type: event, timestamp: Date.now() }
          : { ...event, timestamp: event.timestamp || Date.now() };

      if (window.webkit?.messageHandlers?.handleEvent) {
        window.webkit.messageHandlers.handleEvent.postMessage(eventData);
        return true;
      } else if (window.Android?.handleEvent) {
        // Android might expect a JSON string
        window.Android.handleEvent(JSON.stringify(eventData));
        return true;
      } else {
        console.error('Native bridge not found.');
        return false;
      }
    } catch (error) {
      console.error('Error while sending event to native:', error);
      return false;
    }
  }

  /**
   * Fire verification success event
   */
  fireVerificationSuccessEvent(additionalData?: any): boolean {
    const event: NativeEvent = {
      type: NativeEventType.VERIFICATION_SUCCESS,
      data: additionalData,
    };

    return this.sendEventToNative(event);
  }

  /**
   * Fire video verification success event
   */
  fireVideoVerificationSuccessEvent(additionalData?: any): boolean {
    const event: NativeEvent = {
      type: NativeEventType.VIDEO_VERIFICATION_SUCCESS,
      data: additionalData,
    };

    return this.sendEventToNative(event);
  }

  /**
   * Fire verification step completed event
   */
  fireStepCompletedEvent(stepName: string, additionalData?: any): boolean {
    const event: NativeEvent = {
      type: NativeEventType.VERIFICATION_STEP_COMPLETED,
      data: {
        step: stepName,
        ...additionalData,
      },
    };

    return this.sendEventToNative(event);
  }

  /**
   * Fire custom event
   */
  fireCustomEvent(eventType: string, data?: any): boolean {
    const event: NativeEvent = {
      type: eventType,
      data,
    };

    return this.sendEventToNative(event);
  }
}
